// BinaryTradeTicket — buy YES / buy NO against a binary Maniswap market
// (I-074 P3b). Live price-impact preview via lib/binaryPricing (the TS
// mirror of the binary_quote RPC); submits through the buy_binary RPC.
//
// Presentational + RPC only; self-contained inline styles so it does not
// touch the mobile-jailbreak CSS sharp edges in MultiPage/index.css. Wire
// it into the cockpit feed once a binary market surface exists.
//
// NOTE: live end-to-end behaviour depends on P3a
// (20260615000000_binary_engine_arena_port.sql) being applied to the
// Arena Supabase and at least one operator-minted binary market existing.
// Until then this renders against whatever market prop it is given.

import React, { useMemo, useState } from 'react';
import { supabase, isSupabaseEnabled } from '../lib/supabaseClient';
import { previewBuy, binaryProbYes, Side } from '../lib/binaryPricing';

export interface BinaryMarket {
  market_id: string;
  question: string;
  yes_reserve: number;
  no_reserve: number;
  p: number;
  status: string; // 'OPEN' | 'SETTLED'
}

interface Props {
  market: BinaryMarket;
  userId: string;
  onTraded?: () => void;
}

const pct = (x: number) => `${(x * 100).toFixed(1)}%`;

export default function BinaryTradeTicket({ market, userId, onTraded }: Props) {
  const [side, setSide] = useState<Side>('YES');
  const [stakeStr, setStakeStr] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const stake = Number(stakeStr);
  const open = market.status === 'OPEN';
  const currentYes = binaryProbYes(market.yes_reserve, market.no_reserve, market.p);

  const preview = useMemo(() => {
    if (!Number.isFinite(stake) || stake <= 0) return null;
    return previewBuy(market.yes_reserve, market.no_reserve, market.p, side, stake);
  }, [market.yes_reserve, market.no_reserve, market.p, side, stake]);

  async function submit() {
    setError(null);
    setDone(null);
    if (!isSupabaseEnabled() || !supabase) {
      setError('Trading is offline (Supabase not configured).');
      return;
    }
    if (!preview) {
      setError('Enter a positive stake.');
      return;
    }
    setSubmitting(true);
    try {
      const { data, error: rpcErr } = await supabase.rpc('buy_binary', {
        p_market_id: market.market_id,
        p_user_id: userId,
        p_side: side,
        p_stake: stake,
      });
      if (rpcErr) {
        // negative-balance guard (binary_debits) raises check_violation (23514)
        setError(rpcErr.code === '23514'
          ? 'Insufficient balance for this stake.'
          : rpcErr.message);
        return;
      }
      const shares = data && typeof data === 'object' ? (data as { shares?: number }).shares : undefined;
      setDone(shares != null ? `Filled — ${shares.toFixed(2)} ${side} shares.` : 'Filled.');
      setStakeStr('');
      onTraded?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  const yesActive = side === 'YES';
  return (
    <div style={{
      border: '1px solid rgba(255,255,255,0.15)', borderRadius: 10, padding: 14,
      maxWidth: 360, fontFamily: 'inherit',
    }}>
      <div style={{ fontSize: 13, opacity: 0.8, marginBottom: 6 }}>{market.question}</div>
      <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 10 }}>
        Market P(YES): <strong>{pct(currentYes)}</strong>
        {!open && <span style={{ color: '#e0a', marginLeft: 8 }}>· {market.status}</span>}
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
        {(['YES', 'NO'] as Side[]).map((s) => (
          <button key={s} onClick={() => setSide(s)} disabled={!open}
            style={{
              flex: 1, padding: '8px 0', borderRadius: 8, cursor: open ? 'pointer' : 'default',
              border: '1px solid rgba(255,255,255,0.2)',
              background: side === s ? (s === 'YES' ? '#1f7a4d' : '#7a1f2e') : 'transparent',
              color: 'inherit', fontWeight: side === s ? 700 : 400,
            }}>
            Buy {s}
          </button>
        ))}
      </div>

      <input
        type="number" min="0" step="any" value={stakeStr} placeholder="Stake (OC)"
        onChange={(e) => setStakeStr(e.target.value)} disabled={!open}
        style={{
          width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 8,
          border: '1px solid rgba(255,255,255,0.2)', background: 'transparent', color: 'inherit',
        }} />

      {preview && (
        <div style={{ fontSize: 12, marginTop: 10, lineHeight: 1.6 }}>
          <Row label={`${side} shares`} value={preview.shares.toFixed(2)} />
          <Row label="Effective price" value={pct(preview.effectivePrice)} />
          <Row label="Maker rake (1%)" value={preview.rake.toFixed(2)} />
          <Row label="Price impact"
            value={`${preview.priceImpactPp >= 0 ? '+' : ''}${preview.priceImpactPp.toFixed(2)}pp`} />
          <Row label="P(YES)" value={`${pct(preview.probBefore)} → ${pct(preview.probAfter)}`} />
        </div>
      )}

      <button onClick={submit} disabled={!open || submitting || !preview}
        style={{
          width: '100%', marginTop: 12, padding: '10px 0', borderRadius: 8, fontWeight: 700,
          border: 'none', cursor: (!open || submitting || !preview) ? 'default' : 'pointer',
          background: yesActive ? '#1f7a4d' : '#7a1f2e', color: '#fff',
          opacity: (!open || submitting || !preview) ? 0.5 : 1,
        }}>
        {submitting ? 'Placing…' : `Buy ${side}`}
      </button>

      {error && <div style={{ color: '#f88', fontSize: 12, marginTop: 8 }}>{error}</div>}
      {done && <div style={{ color: '#8f8', fontSize: 12, marginTop: 8 }}>{done}</div>}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <span style={{ opacity: 0.7 }}>{label}</span>
      <span style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</span>
    </div>
  );
}
